import { useState, useRef } from 'react';
import { useAppStore } from '../store';
import Btn from '../ui/Btn';
import Panel from '../ui/Panel';
import Chip from '../ui/Chip';
import SectionTitle from '../ui/SectionTitle';
import { FC_PLAYOFF_ROUNDS, FC_FINAL, FC_THIRD, FC_GROUPS, FC_TOURNAMENT, FC_PARTICIPANTS } from './data';
import { TrophyIcon, BallIcon, Ic, IC, Seg, LiveDot, Flag } from './ui';
import FifaGroupsView from './Groups';
import FifaParticipants from './Participants';

/* ---------- helpers ---------- */
const bkWinner = (m) => (m.a && m.a.win ? m.a.n : m.b && m.b.win ? m.b.n : null);
const bkHas = (m, sel) => !!sel && ((m.a.n === sel) || (m.b.n === sel));
/* сборная по имени игрока */
const BK_TEAM = Object.fromEntries(FC_PARTICIPANTS.map(p => [p.name, p.team]));

/* ---------- статус матча ---------- */
function BkStatus({ m }) {
  if (m.status === 'live') return (
    <span className="row gap-1" style={{ color: 'var(--accent-bright)', fontWeight: 700 }}>
      <LiveDot size={6} />LIVE {m.liveMin ? `· ${m.liveMin}′` : ''}
    </span>
  );
  if (m.status === 'done') return <span style={{ color: 'var(--ok)' }}>✓</span>;
  if (m.status === 'soon') return <span style={{ color: 'var(--warn)' }}>скоро</span>;
  return <span>ожидается</span>;
}

/* ---------- строка игрока ---------- */
function BkRow({ side, m, sel, onSel, showClub }) {
  if (!side.n) {
    return (
      <div className="bk-prow tbd">
        <span className="bk-flag" style={{ opacity: 0.4 }}><BallIcon size={17} color="var(--fg-faint)" /></span>
        <span className="bk-nick" style={{ color: 'var(--fg-faint)', fontStyle: 'italic', fontWeight: 500, fontSize: 12 }}>{side.ph}</span>
        <span className="bk-score" style={{ color: 'var(--fg-faint)' }}>–</span>
      </div>
    );
  }
  const played = m.status === 'done' || m.status === 'live';
  const cls = ['bk-prow',
    side.win ? 'winner' : (m.status === 'done' ? 'loser' : ''),
    sel === side.n ? 'sel' : ''].join(' ');
  return (
    <div className={cls} onClick={() => onSel(side.n)} title={`${side.c} · ${side.n} — подсветить путь`}>
      <span className="bk-flag"><Flag team={side.c} size={22} /></span>
      <span className="col" style={{ minWidth: 0 }}>
        <span className="bk-team display">{side.c}</span>
        {showClub && <span className="bk-player">{side.n} · {side.via}</span>}
      </span>
      <span className="bk-score">{played ? side.g : '–'}</span>
    </div>
  );
}

/* ---------- карточка матча ---------- */
function BkCard({ m, sel, onSel, showClub, width, label }) {
  const involved = bkHas(m, sel);
  const cls = ['bk-card', m.status === 'live' ? 'live' : '', sel ? (involved ? 'hl' : 'dim') : ''].join(' ');
  return (
    <div className={cls} style={{ width }}>
      <div className="bk-card-head">
        <span>{label || m.id} · {m.time}</span>
        <BkStatus m={m} />
      </div>
      <BkRow side={m.a} m={m} sel={sel} onSel={onSel} showClub={showClub} />
      <BkRow side={m.b} m={m} sel={sel} onSel={onSel} showClub={showClub} />
    </div>
  );
}

/* ---------- соединители ---------- */
function BkConn({ nIn, colH, sel, left, right, headH }) {
  const slotH = colH / nIn;
  const pairs = [];
  for (let k = 0; k < nIn / 2; k++) {
    const yT = (2 * k + 0.5) * slotH, yB = (2 * k + 1.5) * slotH, yO = (2 * k + 1) * slotH;
    const recv = right.matches[k];
    const top = left.matches[2 * k], bot = left.matches[2 * k + 1];
    const hl = sel && bkHas(recv, sel) && (bkWinner(top) === sel || bkWinner(bot) === sel);
    pairs.push({ yT, yB, yO, hl });
  }
  return (
    <div style={{ width: 44, flexShrink: 0 }}>
      <div style={{ height: headH }} />
      <svg width="44" height={colH} style={{ display: 'block' }}>
        {pairs.map((p, i) => (
          <g key={i} fill="none"
            stroke={p.hl ? 'var(--gold)' : 'rgba(255,255,255,0.14)'}
            strokeWidth={p.hl ? 2 : 1.4}
            style={{ transition: 'stroke .2s' }}>
            <path d={`M0 ${p.yT} H22 M0 ${p.yB} H22 M22 ${p.yT} V ${p.yB} M22 ${p.yO} H44`} />
          </g>
        ))}
      </svg>
    </div>
  );
}

/* ---------- колонка раунда ---------- */
function BkRoundCol({ round, colH, cardW, sel, onSel, showClub, headH, colRef }) {
  const slotH = colH / round.matches.length;
  return (
    <div ref={colRef} style={{ width: cardW, flexShrink: 0 }}>
      <div className="bk-round-head" style={{ height: headH - 14 }}>
        <div className="bk-round-title">{round.title}</div>
        <div className="bk-round-date">{round.date}</div>
      </div>
      <div className="col">
        {round.matches.map(m => (
          <div key={m.id} style={{ height: slotH, display: 'flex', alignItems: 'center' }}>
            <BkCard m={m} sel={sel} onSel={onSel} showClub={showClub} width={cardW} />
          </div>
        ))}
      </div>
    </div>
  );
}

/* ---------- финалы + чемпион ---------- */
function BkFinalCol({ colH, cardW, sel, onSel, showClub, headH, colRef }) {
  return (
    <div ref={colRef} style={{ width: cardW, flexShrink: 0 }}>
      <div className="bk-round-head" style={{ height: headH - 14 }}>
        <div className="bk-round-title" style={{ color: 'var(--gold)' }}>Финалы</div>
        <div className="bk-round-date">12 ИЮЛ · SALEM</div>
      </div>
      <div style={{ position: 'relative', height: colH }}>
        <div style={{ position: 'absolute', top: colH / 2, left: 0, right: 0, transform: 'translateY(-50%)' }}>
          <div className="mono" style={{ fontSize: 9.5, letterSpacing: '0.22em', color: 'var(--gold)', textTransform: 'uppercase', marginBottom: 6, display: 'flex', alignItems: 'center', gap: 6 }}>
            <TrophyIcon size={13} color="var(--gold)" />{FC_FINAL.label}
          </div>
          <BkCard m={FC_FINAL} sel={sel} onSel={onSel} showClub={showClub} width={cardW} label={FC_FINAL.id} />
        </div>
        <div style={{ position: 'absolute', top: colH / 2 + 118, left: 0, right: 0 }}>
          <div className="mono" style={{ fontSize: 9.5, letterSpacing: '0.22em', color: 'var(--fg-faint)', textTransform: 'uppercase', margin: '6px 0' }}>
            {FC_THIRD.label} · БРОНЗА
          </div>
          <BkCard m={FC_THIRD} sel={sel} onSel={onSel} showClub={showClub} width={cardW} label={FC_THIRD.id} />
        </div>
      </div>
    </div>
  );
}

function BkChampCol({ colH, headH }) {
  const champ = bkWinner(FC_FINAL);
  return (
    <div style={{ width: 190, flexShrink: 0 }}>
      <div style={{ height: headH }} />
      <div style={{ position: 'relative', height: colH }}>
        <div className="col center gap-2" style={{
          position: 'absolute', top: colH / 2, left: 0, right: 0, transform: 'translateY(-50%)',
          padding: '26px 16px', textAlign: 'center', borderRadius: 'var(--r-lg)',
          background: 'linear-gradient(160deg, rgba(230,183,60,0.12), var(--surface))',
          border: '1px solid rgba(230,183,60,0.35)', boxShadow: 'var(--shadow-card), var(--glow-gold)' }}>
          <TrophyIcon size={34} color="var(--gold)" />
          <span className="display" style={{ fontSize: 19, color: 'var(--gold)' }}>Чемпион</span>
          {champ ? (
            <span className="col center gap-2">
              <Flag team={BK_TEAM[champ]} size={44} />
              <span className="display" style={{ fontSize: 18 }}>{BK_TEAM[champ]}</span>
              <span className="mono" style={{ fontSize: 10, letterSpacing: '0.14em', color: 'var(--fg-muted)', textTransform: 'uppercase' }}>{champ}</span>
            </span>
          ) : (
            <span style={{ fontSize: 12, color: 'var(--fg-muted)', lineHeight: 1.5 }}>
              Определится 12 июля в спортбаре SALEM
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

/* ---------- плей-офф ---------- */
function FCPlayoffView({ density }) {
  const [sel, setSel] = useState(null);
  const wrapRef = useRef(null);
  const col0 = useRef(null), col1 = useRef(null), col2 = useRef(null), col3 = useRef(null);
  const colRefs = [col0, col1, col2, col3];

  const comfort = density !== 'compact';
  const rowH = comfort ? 126 : 100;
  const cardW = comfort ? 258 : 220;
  const showClub = comfort;
  const colH = rowH * 8;
  const headH = 52;

  const onSel = (n) => setSel(s => (s === n ? null : n));
  const jump = (i) => {
    const w = wrapRef.current, c = colRefs[i].current;
    if (w && c) w.scrollTo({ left: c.offsetLeft - w.offsetLeft - 12, behavior: 'smooth' });
  };

  return (
    <div>
      <div className="row gap-3 wrap" style={{ marginBottom: 16, justifyContent: 'space-between' }}>
        <div className="bk-tabs">
          {['1/8', '1/4', '1/2', 'Финалы'].map((t, i) => (
            <Btn key={t} size="sm" variant="dark" onClick={() => jump(i)}>{t}</Btn>
          ))}
        </div>
        {sel ? (
          <Chip color="var(--gold)" icon={<BallIcon size={12} />} style={{ cursor: 'pointer' }}>
            <span onClick={() => setSel(null)}>Путь: {BK_TEAM[sel] || sel} · сбросить ✕</span>
          </Chip>
        ) : (
          <div className="bk-legend">
            <span><span style={{ color: 'var(--warn)' }}>●</span> пара известна</span>
            <span><span style={{ color: 'var(--fg-faint)' }}>●</span> ждёт группы / победителей</span>
            <span><span style={{ color: 'var(--gold)' }}>—</span> клик по сборной = её путь</span>
          </div>
        )}
      </div>

      <div className="bk-wrap" ref={wrapRef}>
        <div className="bk-inner">
          <BkRoundCol round={FC_PLAYOFF_ROUNDS[0]} colH={colH} cardW={cardW} sel={sel} onSel={onSel} showClub={showClub} headH={headH} colRef={colRefs[0]} />
          <BkConn nIn={8} colH={colH} sel={sel} left={FC_PLAYOFF_ROUNDS[0]} right={FC_PLAYOFF_ROUNDS[1]} headH={headH} />
          <BkRoundCol round={FC_PLAYOFF_ROUNDS[1]} colH={colH} cardW={cardW} sel={sel} onSel={onSel} showClub={showClub} headH={headH} colRef={colRefs[1]} />
          <BkConn nIn={4} colH={colH} sel={sel} left={FC_PLAYOFF_ROUNDS[1]} right={FC_PLAYOFF_ROUNDS[2]} headH={headH} />
          <BkRoundCol round={FC_PLAYOFF_ROUNDS[2]} colH={colH} cardW={cardW} sel={sel} onSel={onSel} showClub={showClub} headH={headH} colRef={colRefs[2]} />
          <BkConn nIn={2} colH={colH} sel={sel} left={FC_PLAYOFF_ROUNDS[2]} right={{ matches: [FC_FINAL] }} headH={headH} />
          <BkFinalCol colH={colH} cardW={cardW} sel={sel} onSel={onSel} showClub={showClub} headH={headH} colRef={colRefs[3]} />
          <div style={{ width: 34, flexShrink: 0 }}>
            <div style={{ height: headH }} />
            <svg width="34" height={colH} style={{ display: 'block' }}>
              <path d={`M0 ${colH / 2} H34`} fill="none" stroke="rgba(230,183,60,0.35)" strokeWidth="1.4" strokeDasharray="4 4" />
            </svg>
          </div>
          <BkChampCol colH={colH} headH={headH} />
        </div>
      </div>

      <p style={{ margin: '14px 2px 0', fontSize: 12.5, color: 'var(--fg-faint)' }}>
        1/8 играется в две волны по 4 стола. Ничья — доп. время и пенальти; полуфиналы и финал — серия до 2 побед. Клик по игроку подсвечивает его путь.
      </p>
    </div>
  );
}

/* ---------- верхняя полоса ---------- */
function BkStrip({ go }) {
  const liveGroups = FC_GROUPS.filter(g => g.live);
  const items = liveGroups.length ? [
    { k: 'live', head: <span className="row gap-2" style={{ color: 'var(--accent-bright)' }}><LiveDot size={6} />СЕЙЧАС ИГРАЮТ</span>,
      body: `${liveGroups.length} матча идут`, sub: 'Столы 1–4 · групповой этап', act: <Btn size="sm" variant="outline" onClick={() => go('stream')}>Эфир →</Btn> },
    { k: 'po', head: <span style={{ color: 'var(--warn)' }}>ПЛЕЙ-ОФФ · 1/8</span>, body: '16 игроков', sub: 'по два лучших из каждой группы' },
    { k: 'final', head: <span style={{ color: 'var(--gold)' }}>ФИНАЛЫ · LAN</span>, body: 'SALEM', sub: '12 ИЮЛ · полуфиналы, бронза, финал BO3' },
  ] : [
    { k: 'groups', head: <span style={{ color: 'var(--ok)' }}>ГРУППЫ · СЫГРАНЫ ✓</span>,
      body: '8 групп', sub: 'по два лучших вышли в плей-офф', act: <Btn size="sm" variant="outline" onClick={() => go('stream')}>Эфир →</Btn> },
    { k: 'po', head: <span className="row gap-2" style={{ color: 'var(--accent-bright)' }}><LiveDot size={6} />ПЛЕЙ-ОФФ · 1/8</span>, body: '16 игроков', sub: 'пары определены · 2 волны по 4 стола' },
    { k: 'final', head: <span style={{ color: 'var(--gold)' }}>ФИНАЛЫ · LAN</span>, body: 'SALEM', sub: 'полуфиналы, бронза, финал BO3' },
  ];
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 14, marginBottom: 24 }} className="bk-strip">
      {items.map(it => (
        <Panel key={it.k} pad={16} style={it.k === 'live' ? { borderColor: 'rgba(225,18,23,0.5)' } : undefined}>
          <div className="row" style={{ justifyContent: 'space-between', alignItems: 'flex-start', gap: 10 }}>
            <div className="col" style={{ minWidth: 0 }}>
              <div className="mono" style={{ fontSize: 10, letterSpacing: '0.18em' }}>{it.head}</div>
              <div className="display" style={{ fontSize: 21, margin: '8px 0 3px' }}>{it.body}</div>
              <div style={{ fontSize: 12, color: 'var(--fg-muted)' }}>{it.sub}</div>
            </div>
            {it.act}
          </div>
        </Panel>
      ))}
    </div>
  );
}

/* ---------- страница ---------- */
export default function FifaBracketPage({ density = 'comfort', initialTab = 'playoff' }) {
  const { go } = useAppStore();
  const [tab, setTab] = useState(initialTab);
  return (
    <main className="page-in">
      <section style={{ padding: '44px 0 56px' }}>
        <div className="container-wide">
          <SectionTitle eyebrow="Формат чемпионата мира" title="Турнирная сетка"
            sub="Сначала — 8 групп, каждый с каждым внутри группы. Затем — плей-офф с 1/8 финала до кубка. Сборные раздаёт жеребьёвка в день турнира." />
          <BkStrip go={go} />
          <div style={{ marginBottom: 22 }}>
            <Seg value={tab} onChange={setTab} options={[
              { value: 'groups', label: 'Группы', icon: <Ic d={IC.grid} size={15} /> },
              { value: 'playoff', label: 'Плей-офф', icon: <TrophyIcon size={15} /> },
              { value: 'players', label: 'Участники', icon: <Ic d={IC.users} size={15} /> },
            ]} />
          </div>
          {tab === 'groups' && <FifaGroupsView />}
          {tab === 'playoff' && <FCPlayoffView density={density} />}
          {tab === 'players' && <FifaParticipants />}
        </div>
      </section>
    </main>
  );
}
